import {
	type Dispatch,
	type FocusEvent,
	type HTMLProps,
	type ButtonHTMLAttributes,
	type SetStateAction,
	createContext,
	use,
	useEffect,
	useRef,
	useState,
	type MouseEvent,
	type ReactNode,
	type FC,
} from "react";
import styled, { css } from "styled-components";
import {
	autoUpdate,
	flip,
	FloatingFocusManager,
	FloatingList,
	FloatingNode,
	FloatingPortal,
	FloatingTree,
	offset,
	safePolygon,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useFloatingNodeId,
	useFloatingParentNodeId,
	useFloatingTree,
	useHover,
	useInteractions,
	useListItem,
	useListNavigation,
	useMergeRefs,
	useRole,
	useTypeahead,
} from "@floating-ui/react";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";

import { STYLES } from "@vita-learning/styles";

import { Icon } from "../../widgets";

const DropdownTree: FC<
	Omit<HTMLProps<HTMLButtonElement>, keyof DropdownProps> & DropdownProps
> = ({
	children,
	items,
	hasBorder = false,
	highlightInteraction = false,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [hasFocusInside, setHasFocusInside] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);

	const elementsRef = useRef<(HTMLButtonElement | null)[]>([]);
	const labelsRef = useRef<(string | null)[]>([]);
	const parent = use(DropdownContext);

	const tree = useFloatingTree();
	const nodeId = useFloatingNodeId();
	const parentId = useFloatingParentNodeId();
	const item = useListItem();

	const isNested = parentId !== null;

	const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
		nodeId,
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: isNested ? "right-start" : "bottom-start",
		middleware: [
			offset({ mainAxis: isNested ? 0 : 4, alignmentAxis: isNested ? -4 : 0 }),
			flip(),
			shift(),
		],
		whileElementsMounted: autoUpdate,
	});

	const hover = useHover(context, {
		enabled: isNested,
		delay: { open: 75 },
		handleClose: safePolygon({ blockPointerEvents: true }),
	});
	const click = useClick(context, {
		event: "mousedown",
		toggle: !isNested,
		ignoreMouse: isNested,
	});
	const role = useRole(context, { role: "menu" });
	const dismiss = useDismiss(context, { bubbles: true });
	const listNavigation = useListNavigation(context, {
		listRef: elementsRef,
		activeIndex,
		nested: isNested,
		onNavigate: setActiveIndex,
	});
	const typeahead = useTypeahead(context, {
		listRef: labelsRef,
		onMatch: isOpen ? setActiveIndex : undefined,
		activeIndex,
	});

	const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
		[hover, click, role, dismiss, listNavigation, typeahead]
	);

	useEffect(() => {
		if (!tree) return;

		const handleTreeClick = () => setIsOpen(false);

		const onSubMenuOpen = (event: Record<"nodeId" | "parentId", string>) => {
			if (event.nodeId === nodeId || event.parentId !== parentId) return;

			setIsOpen(false);
		};

		tree.events.on("click", handleTreeClick);
		tree.events.on("menuopen", onSubMenuOpen);

		return () => {
			tree.events.off("click", handleTreeClick);
			tree.events.off("menuopen", onSubMenuOpen);
		};
	}, [tree, nodeId, parentId]);

	useEffect(() => {
		if (!isOpen || !tree) return;

		tree.events.emit("menuopen", { parentId, nodeId });
	}, [tree, isOpen, nodeId, parentId]);

	return (
		<FloatingNode id={nodeId}>
			<TreeItem
				{...props}
				ref={useMergeRefs([refs.setReference, item.ref, props.ref])}
				tabIndex={
					!isNested ? undefined : parent.activeIndex === item.index ? 0 : -1
				}
				role={isNested ? "menuitem" : undefined}
				data-open={isOpen ? "" : undefined}
				data-nested={isNested ? "" : undefined}
				data-focus-inside={hasFocusInside ? "" : undefined}
				$isNested={isNested}
				$hasBorder={hasBorder}
				$highlightInteraction={highlightInteraction}
				type={props.type as "button" | "reset" | "submit"}
				{...getReferenceProps(
					parent.getItemProps({
						...props,
						onFocus(event: FocusEvent<HTMLButtonElement>) {
							props.onFocus?.(event);
							setHasFocusInside(false);
							parent.setHasFocusInside(true);
						},
					})
				)}
			>
				{children}
				{isNested && (
					<span aria-hidden style={{ marginLeft: 10, fontSize: 10 }}>
						<Icon icon={faCaretRight} />
					</span>
				)}
			</TreeItem>
			<DropdownContext
				value={{
					activeIndex,
					setActiveIndex,
					getItemProps,
					setHasFocusInside,
					isOpen,
				}}
			>
				<FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
					{isOpen && (
						<FloatingPortal>
							<FloatingFocusManager
								context={context}
								modal={false}
								initialFocus={isNested ? -1 : 0}
								returnFocus={!isNested}
							>
								<Menu
									ref={refs.setFloating}
									style={floatingStyles}
									{...getFloatingProps()}
								>
									{items.map(p => (
										<DropdownItem key={p.label} {...p} />
									))}
								</Menu>
							</FloatingFocusManager>
						</FloatingPortal>
					)}
				</FloatingList>
			</DropdownContext>
		</FloatingNode>
	);
};

interface DropdownContext {
	getItemProps: (userProps?: HTMLProps<HTMLElement>) => Record<string, unknown>;
	activeIndex: number | null;
	setActiveIndex: Dispatch<SetStateAction<number | null>>;
	setHasFocusInside: Dispatch<SetStateAction<boolean>>;
	isOpen: boolean;
}

const DropdownContext = createContext<DropdownContext>({
	getItemProps: () => ({}),
	activeIndex: null,
	setActiveIndex: () => {},
	setHasFocusInside: () => {},
	isOpen: false,
});

export interface DropdownProps {
	readonly items: DropdownItemProps[];
	readonly hasBorder?: boolean;
	readonly highlightInteraction?: boolean;
}

export const Dropdown: FC<
	Omit<HTMLProps<HTMLButtonElement>, keyof DropdownProps> & DropdownProps
> = props => {
	const parentId = useFloatingParentNodeId();

	if (parentId === null)
		return (
			<FloatingTree>
				<DropdownTree {...props} />
			</FloatingTree>
		);

	return <DropdownTree {...props} />;
};

export interface DropdownItemProps
	extends Pick<
		ButtonHTMLAttributes<HTMLButtonElement>,
		"onClick" | "disabled" | "onFocus"
	> {
	readonly label: string;
	readonly icon?: IconDefinition;
	readonly items?: DropdownItemProps[];
	readonly renderItem?: (
		props: Omit<DropdownItemProps, "renderItem">
	) => ReactNode;
}

type DropdownLabelProps = DropdownItemProps;

const DropdownLabel: FC<DropdownLabelProps> = ({
	label,
	icon,
	items,
	renderItem,
}) =>
	renderItem?.({ label, icon, items }) || (
		<>
			{icon && (
				<>
					<Icon icon={icon} />
					&nbsp;
				</>
			)}
			{label}
			{items && (
				<>
					&nbsp;
					<Icon icon={faCaretRight} />
				</>
			)}
		</>
	);

const DropdownItem: FC<
	Omit<HTMLProps<HTMLButtonElement>, keyof DropdownItemProps> &
		DropdownItemProps
> = ({ label, disabled, items, renderItem, icon, ...props }) => {
	const dropdown = use(DropdownContext);
	const item = useListItem({ label: disabled ? null : label });
	const tree = useFloatingTree();
	const isActive = item.index === dropdown.activeIndex;

	return (
		<Button
			{...props}
			ref={useMergeRefs([item.ref, props.ref])}
			type="button"
			role="menuitem"
			tabIndex={isActive ? 0 : -1}
			disabled={disabled}
			{...dropdown.getItemProps({
				onClick(event: MouseEvent<HTMLButtonElement>) {
					props.onClick?.(event);
					tree?.events.emit("click");
				},
				onFocus(event: FocusEvent<HTMLButtonElement>) {
					props.onFocus?.(event);
					dropdown.setHasFocusInside(true);
				},
			})}
		>
			{items?.length ? (
				<Dropdown items={items}>
					<DropdownLabel {...{ items, label, icon, renderItem }} />
				</Dropdown>
			) : (
				<DropdownLabel {...{ items, label, icon, renderItem }} />
			)}
		</Button>
	);
};

DropdownItem.displayName = "DropdownItem";

const Button = styled.button`
	align-items: center;
	background: none;
	cursor: pointer;
	display: flex;
	border: none;
	border-radius: 0.25rem;
	justify-content: space-between;
	line-height: 1.8;
	margin: 0;
	outline: 0;
	width: 100%;

	&:focus,
	&[data-nested][data-open]:not([data-focus-inside]),
	&[data-focus-inside][data-open] {
		background: ${STYLES.colors.lights.backgrounds.blanks.interaction};
	}
`;

const Menu = styled.div`
  background: rgba(255, 255, 255, 0.8);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  padding: 0.25rem;
  border-radius: 6px;
  box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.1);
  outline: 0;
	z-index: 91;
`;

interface InteractiveNode {
	$highlightInteraction: boolean;
}

const nestedStyles = css<InteractiveNode>`
	line-height: 1.8;
	margin: 0;
	text-align: left;
	width: 100%;

	&:focus,
	&[data-nested][data-open]:not([data-focus-inside]),
	&[data-focus-inside][data-open] {
		${({ $highlightInteraction }) => $highlightInteraction && `background: ${STYLES.colors.lights.backgrounds.blanks.interaction};`}
	}
`;

interface BorderedNode {
	$hasBorder: boolean;
}

const rootStyles = css<BorderedNode & InteractiveNode>`
	border: ${({ $hasBorder }) => ($hasBorder ? `1px solid ${STYLES.colors.lights.borders.dark}` : "none")};
	
	&[data-open],
	&:hover {
		${({ $highlightInteraction }) => $highlightInteraction && `background: ${STYLES.colors.lights.backgrounds.blanks.interaction};`}
		border: ${({ $hasBorder }) => ($hasBorder ? `1px solid ${STYLES.colors.lights.borders.dark}` : "none")};
	}
`;

const TreeItem = styled.button<
	{ $isNested: boolean } & BorderedNode & InteractiveNode
>`
	align-items: center;
	background: none;
	border: none;
	border-radius: 0.33rem;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	outline: none;
	padding: 0.25rem 0.5rem;

	${({ $isNested }) => ($isNested ? nestedStyles : rootStyles)}
`;
